import React, { useState } from 'react';
import useEscapeKey from '../hooks/useEscapeKey';

export default function AddressModal({ employee, onSave, onClose }) {
  useEscapeKey(onClose);
  const [form, setForm] = useState({
    address: employee.address || '',
    address_line_2: employee.address_line_2 || '',
    city: employee.city || '',
    postcode: employee.postcode || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const UK_POSTCODE_REGEX = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.address.trim()) {
      setError('Address line 1 is required');
      return;
    }
    if (!form.city.trim()) {
      setError('City / Town is required');
      return;
    }
    if (!form.postcode.trim()) {
      setError('Postcode is required');
      return;
    }
    if (!UK_POSTCODE_REGEX.test(form.postcode.trim())) {
      setError('Please enter a valid UK postcode (e.g. SW1A 2AA)');
      return;
    }
    setSaving(true);
    try {
      await onSave(employee.id, form);
      onClose();
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const hasChanged = form.address !== (employee.address || '') ||
    form.address_line_2 !== (employee.address_line_2 || '') ||
    form.city !== (employee.city || '') ||
    form.postcode !== (employee.postcode || '');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Change Address - {employee.first_name} {employee.last_name}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 active:bg-gray-200 rounded-full p-2 text-2xl leading-none" aria-label="Close">&times;</button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {employee.address && (
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm">
              <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Current Address</p>
              <p className="text-gray-600 dark:text-gray-400">{employee.address}</p>
              {employee.address_line_2 && <p className="text-gray-600 dark:text-gray-400">{employee.address_line_2}</p>}
              {employee.city && <p className="text-gray-600 dark:text-gray-400">{employee.city}</p>}
              <p className="text-gray-600 dark:text-gray-400">{employee.postcode}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Address Line 1 *</label>
              <input type="text" name="address" value={form.address} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="e.g. 10 Downing Street" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Address Line 2</label>
              <input type="text" name="address_line_2" value={form.address_line_2} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="e.g. Westminster" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">City / Town *</label>
                <input type="text" name="city" value={form.city} onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="e.g. London" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Postcode *</label>
                <input type="text" name="postcode" value={form.postcode} onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="e.g. SW1A 2AA" />
              </div>
            </div>

            {hasChanged && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-300">
                Address will be updated and flagged for payroll processing.
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
                Cancel
              </button>
              <button type="submit" disabled={saving || !hasChanged}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Update Address'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
